/**
 * AI-verbruik & kosten (US-041).
 *
 * Twee soorten getallen die bewust gescheiden blijven: het **verbruik** is een meting die uit het
 * antwoord van de dienst komt, de **kosten** zijn een schatting op basis van tarieven die de
 * beheerder invult. Zolang er geen tarief is, blijven de kosten leeg in plaats van dat er een
 * bedrag wordt verzonnen.
 *
 * Zoekopdrachten op het web staan apart, omdat ze per stuk worden afgerekend en niet in het
 * tokental meetellen. Zonder dat aparte getal lijkt zoeken bijna gratis.
 */

import { formatEuro } from '@prijsmonitor/shared';
import { useCallback, useEffect, useState } from 'react';

import { api, type ApiError } from '../api/client.js';
import { Button, Card, Chip, Counter, Notice, ui } from '../components/ui/index.js';
import styles from './AiUsageScreen.module.css';

type Periode = 'dag' | 'maand' | 'jaar';

interface PeriodeRij {
  periode: string;
  aanroepen: number;
  mislukt: number;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  webSearchCalls: number;
  costMicros: number;
  kostenOnvolledig: boolean;
}

interface Uitsplitsing {
  label: string;
  aanroepen: number;
  totalTokens: number;
  webSearchCalls: number;
  costMicros: number;
  kostenOnvolledig: boolean;
}

interface Tarief {
  model: string;
  inputPerMillion: number;
  cachedPerMillion: number;
  outputPerMillion: number;
  webSearchPerCall: number;
}

interface Overzicht {
  periode: Periode;
  rijen: PeriodeRij[];
  totaal: PeriodeRij;
  perModel: Uitsplitsing[];
  perSoort: Uitsplitsing[];
  perProduct: Uitsplitsing[];
  tarieven: Tarief[];
  modellenZonderTarief: string[];
  huidigModel: string;
  aiIngeschakeld: boolean;
}

const MICROS_PER_EURO = 1_000_000;

/** Duizendtallen met een punt, zoals overal in de applicatie. */
function getal(waarde: number): string {
  return waarde.toLocaleString('nl-NL');
}

/**
 * Kosten in euro's uit miljoenste euro's.
 *
 * Er wordt met drie decimalen getoond zolang het bedrag onder een euro blijft: een zoekopdracht
 * kost een fractie van een cent, en afronden op centen zou daar structureel € 0,00 van maken.
 */
function kosten(micros: number, onvolledig: boolean): string {
  if (micros === 0 && onvolledig) return 'onbekend';
  const euro = micros / MICROS_PER_EURO;
  const bedrag =
    euro > 0 && euro < 1
      ? `€ ${euro.toFixed(3).replace('.', ',')}`
      : formatEuro(Math.round(euro * 100));
  return onvolledig ? `${bedrag} +?` : bedrag;
}

/** Tarief van miljoenste euro's naar een invoerwaarde in euro's. */
function naarEuro(micros: number): string {
  return String(micros / MICROS_PER_EURO);
}

const PERIODE_LABEL: Record<Periode, string> = {
  dag: 'Per dag',
  maand: 'Per maand',
  jaar: 'Per jaar',
};

export function AiUsageScreen(): React.JSX.Element {
  const [periode, setPeriode] = useState<Periode>('dag');
  const [data, setData] = useState<Overzicht | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [tariefModel, setTariefModel] = useState('');
  const [invoer, setInvoer] = useState({
    inputPerMillion: '',
    cachedPerMillion: '',
    outputPerMillion: '',
    webSearchPerCall: '',
  });

  const load = useCallback(async (welke: Periode) => {
    try {
      const overzicht = await api.get<Overzicht>(`/api/ai-verbruik?periode=${welke}`);
      setData(overzicht);
      setTariefModel((huidig) => huidig || overzicht.huidigModel);
    } catch (e) {
      setError((e as ApiError).message ?? 'Het overzicht kon niet worden geladen.');
    }
  }, []);

  useEffect(() => {
    void load(periode);
  }, [load, periode]);

  /** Vult het formulier met het bestaande tarief van het gekozen model. */
  useEffect(() => {
    const bestaand = data?.tarieven.find((t) => t.model === tariefModel);
    setInvoer({
      inputPerMillion: bestaand ? naarEuro(bestaand.inputPerMillion) : '',
      cachedPerMillion: bestaand ? naarEuro(bestaand.cachedPerMillion) : '',
      outputPerMillion: bestaand ? naarEuro(bestaand.outputPerMillion) : '',
      webSearchPerCall: bestaand ? naarEuro(bestaand.webSearchPerCall) : '',
    });
  }, [data, tariefModel]);

  async function bewaarTarief(): Promise<void> {
    setBusy('tarief');
    setError(null);
    setNotice(null);
    try {
      const resultaat = await api.put<{ melding: string }>('/api/ai-verbruik/tarief', {
        model: tariefModel.trim(),
        inputPerMillion: Number(invoer.inputPerMillion || 0),
        cachedPerMillion: Number(invoer.cachedPerMillion || 0),
        outputPerMillion: Number(invoer.outputPerMillion || 0),
        webSearchPerCall: Number(invoer.webSearchPerCall || 0),
      });
      setNotice(resultaat.melding);
      await load(periode);
    } catch (e) {
      setError((e as ApiError).message ?? 'Het tarief kon niet worden opgeslagen.');
    } finally {
      setBusy(null);
    }
  }

  const totaal = data?.totaal;

  return (
    <div className={ui.page}>
      {error && <Notice tone="error">{error}</Notice>}
      {notice && <Notice tone="success">{notice}</Notice>}

      {data && !data.aiIngeschakeld && (
        <Notice tone="info">
          Er is geen AI-dienst ingesteld. Wat hieronder staat is het verbruik van eerdere aanroepen;
          er komt niets meer bij zolang AI_ENABLED uitstaat.
        </Notice>
      )}

      {data && totaal && totaal.aanroepen === 0 && (
        <Notice tone="info">
          Er is nog geen AI-dienst gebruikt. Zodra er kandidaten worden gezocht, verschijnt het
          verbruik hier.
        </Notice>
      )}

      <Card
        title="Totaal"
        description="Verbruik komt uit het antwoord van de dienst en is een meting. Kosten zijn een schatting op basis van de tarieven hieronder."
      >
        <div className={ui.counterRow}>
          <Counter label="Aanroepen" value={getal(totaal?.aanroepen ?? 0)} />
          <Counter label="Tokens totaal" value={getal(totaal?.totalTokens ?? 0)} />
          <Counter label="Zoekopdrachten" value={getal(totaal?.webSearchCalls ?? 0)} />
          <Counter
            label="Geschatte kosten"
            value={kosten(totaal?.costMicros ?? 0, totaal?.kostenOnvolledig ?? false)}
          />
        </div>

        {totaal && totaal.mislukt > 0 && (
          <p className={ui.hint} style={{ marginTop: 10 }}>
            {totaal.mislukt} van de {totaal.aanroepen} aanroepen is mislukt. Die staan er bewust
            bij: een mislukte aanroep kan ook zijn afgerekend.
          </p>
        )}

        {data && data.modellenZonderTarief.length > 0 && (
          <Notice tone="warn">
            Voor {data.modellenZonderTarief.join(', ')} is geen tarief ingevuld, dus die aanroepen
            zitten niet in de kosten — vandaar de “+?”. Vul het tarief in om ze mee te rekenen; dat
            geldt dan voor aanroepen vanaf dat moment.
          </Notice>
        )}
      </Card>

      <Card
        title="Verloop"
        actions={
          <div className={styles.periodeKeuze} role="tablist">
            {(['dag', 'maand', 'jaar'] as Periode[]).map((optie) => (
              <button
                key={optie}
                type="button"
                role="tab"
                aria-selected={periode === optie}
                className={periode === optie ? styles.periodeActief : styles.periode}
                onClick={() => setPeriode(optie)}
              >
                {PERIODE_LABEL[optie]}
              </button>
            ))}
          </div>
        }
      >
        {data && data.rijen.length === 0 ? (
          <p className={ui.hint}>Nog geen verbruik om te tonen.</p>
        ) : (
          <div className={ui.tableWrap}>
            <table className={ui.table}>
              <thead>
                <tr>
                  <th>Periode</th>
                  <th className={ui.numeric}>Aanroepen</th>
                  <th className={ui.numeric}>Invoer</th>
                  <th className={ui.numeric}>Waarvan cache</th>
                  <th className={ui.numeric}>Uitvoer</th>
                  <th className={ui.numeric}>Waarvan redeneren</th>
                  <th className={ui.numeric}>Zoekopdrachten</th>
                  <th className={ui.numeric}>Kosten</th>
                </tr>
              </thead>
              <tbody>
                {data?.rijen.map((rij) => (
                  <tr key={rij.periode}>
                    <td>{rij.periode}</td>
                    <td className={ui.numeric}>
                      {getal(rij.aanroepen)}
                      {rij.mislukt > 0 && (
                        <span className={styles.mislukt}> ({rij.mislukt} mislukt)</span>
                      )}
                    </td>
                    <td className={ui.numeric}>{getal(rij.inputTokens)}</td>
                    <td className={ui.numeric}>{getal(rij.cachedTokens)}</td>
                    <td className={ui.numeric}>{getal(rij.outputTokens)}</td>
                    <td className={ui.numeric}>{getal(rij.reasoningTokens)}</td>
                    <td className={ui.numeric}>{getal(rij.webSearchCalls)}</td>
                    <td className={ui.numeric}>{kosten(rij.costMicros, rij.kostenOnvolledig)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className={styles.uitsplitsingen}>
        {(
          [
            ['Per model', data?.perModel ?? []],
            ['Per soort aanroep', data?.perSoort ?? []],
            ['Per product', data?.perProduct ?? []],
          ] as Array<[string, Uitsplitsing[]]>
        ).map(([titel, rijen]) => (
          <Card
            key={titel}
            title={titel}
            description={
              titel === 'Per product'
                ? 'Waar de kosten door ontstaan: één zoekopdracht per product, en hoeveel dat product er heeft gekost.'
                : undefined
            }
          >
            {rijen.length === 0 ? (
              <p className={ui.hint}>Nog niets vastgelegd.</p>
            ) : (
              <div className={ui.tableWrap}>
                <table className={ui.table}>
                  <thead>
                    <tr>
                      <th>{titel.replace('Per ', '').replace(/^./, (c) => c.toUpperCase())}</th>
                      <th className={ui.numeric}>Aanroepen</th>
                      <th className={ui.numeric}>Tokens</th>
                      <th className={ui.numeric}>Zoeken</th>
                      <th className={ui.numeric}>Kosten</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rijen.map((rij) => (
                      <tr key={rij.label}>
                        <td>{rij.label}</td>
                        <td className={ui.numeric}>{getal(rij.aanroepen)}</td>
                        <td className={ui.numeric}>{getal(rij.totalTokens)}</td>
                        <td className={ui.numeric}>{getal(rij.webSearchCalls)}</td>
                        <td className={ui.numeric}>
                          {kosten(rij.costMicros, rij.kostenOnvolledig)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ))}
      </div>

      <Card
        title="Tarieven"
        description="OpenAI zet geen prijs in het antwoord; de kosten worden hier zelf berekend. Vul de tarieven in zoals ze op de prijslijst van OpenAI staan, in euro's."
      >
        <div className={styles.tariefRij}>
          <label className={ui.hint}>
            Model
            <input
              className={styles.input}
              value={tariefModel}
              onChange={(e) => setTariefModel(e.target.value)}
              placeholder="bijv. gpt-5-mini"
            />
          </label>
          <label className={ui.hint}>
            Invoer per miljoen tokens
            <input
              className={styles.input}
              type="number"
              step="0.001"
              min="0"
              value={invoer.inputPerMillion}
              onChange={(e) => setInvoer((h) => ({ ...h, inputPerMillion: e.target.value }))}
            />
          </label>
          <label className={ui.hint}>
            Uit cache per miljoen tokens
            <input
              className={styles.input}
              type="number"
              step="0.001"
              min="0"
              value={invoer.cachedPerMillion}
              onChange={(e) => setInvoer((h) => ({ ...h, cachedPerMillion: e.target.value }))}
            />
          </label>
          <label className={ui.hint}>
            Uitvoer per miljoen tokens
            <input
              className={styles.input}
              type="number"
              step="0.001"
              min="0"
              value={invoer.outputPerMillion}
              onChange={(e) => setInvoer((h) => ({ ...h, outputPerMillion: e.target.value }))}
            />
          </label>
          <label className={ui.hint}>
            Per zoekopdracht op het web
            <input
              className={styles.input}
              type="number"
              step="0.001"
              min="0"
              value={invoer.webSearchPerCall}
              onChange={(e) => setInvoer((h) => ({ ...h, webSearchPerCall: e.target.value }))}
            />
          </label>
          <Button
            onClick={() => void bewaarTarief()}
            busy={busy === 'tarief'}
            disabled={!tariefModel.trim()}
          >
            Tarief opslaan
          </Button>
        </div>

        <p className={ui.hint} style={{ marginTop: 10 }}>
          Er staan geen tarieven voorgevuld, en dat is opzettelijk: prijzen van OpenAI veranderen en
          een verouderde aanname zou een bedrag opleveren dat betrouwbaar lijkt maar het niet is.
          Zoek ze op bij OpenAI en vul ze hier in. Let op dat OpenAI in dollars rekent — reken zelf
          om, of vul de dollarbedragen in en lees de kosten als dollars.
        </p>
        <p className={ui.hint}>
          Een tarief geldt voor aanroepen vanaf het moment dat u het opslaat. Eerdere aanroepen
          houden de schatting die op dát moment gold, zodat de geschiedenis niet verandert. Het
          tokenverbruik blijft altijd bewaard, dus opnieuw rekenen kan.
        </p>

        {data && data.tarieven.length > 0 && (
          <div className={ui.tableWrap} style={{ marginTop: 12 }}>
            <table className={ui.table}>
              <thead>
                <tr>
                  <th>Model</th>
                  <th className={ui.numeric}>Invoer / M</th>
                  <th className={ui.numeric}>Cache / M</th>
                  <th className={ui.numeric}>Uitvoer / M</th>
                  <th className={ui.numeric}>Zoeken / stuk</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.tarieven.map((tarief) => (
                  <tr key={tarief.model}>
                    <td>{tarief.model}</td>
                    <td className={ui.numeric}>{naarEuro(tarief.inputPerMillion)}</td>
                    <td className={ui.numeric}>{naarEuro(tarief.cachedPerMillion)}</td>
                    <td className={ui.numeric}>{naarEuro(tarief.outputPerMillion)}</td>
                    <td className={ui.numeric}>{naarEuro(tarief.webSearchPerCall)}</td>
                    <td>
                      {tarief.model === data.huidigModel ? (
                        <Chip tone="success">in gebruik</Chip>
                      ) : (
                        <Chip tone="muted">niet in gebruik</Chip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
